import React from "react"
import {
  ActionItemTargetMode,
  DirectionFromOrigin,
  Interval,
  type LegalEntityPermission,
  useListActionItemsBadgeCountsQuery,
  useNotificationCreatedSubscription,
} from "@digits-graphql/frontend/graphql-bearer"
import dateTimeHelper from "@digits-shared/helpers/dateTimeHelper"
import { defined } from "@digits-shared/helpers/filters"
import { usePollingWithBackoff } from "@digits-shared/hooks/usePollingWithBackoff"
import useSession from "@digits-shared/hooks/useSession"
import { AspectCode } from "@digits-shared/session/AspectCode"
import type Permissions from "@digits-shared/session/Permissions"
import { STATUS_OPEN } from "src/frontend/components/OS/Applications/Inbox/ActionItems/constants"
import { useViewVersion } from "src/frontend/components/Shared/Contexts/useViewVersion"
import type FrontendSession from "src/frontend/session/FrontendSession"
import type SessionLegalEntity from "src/frontend/session/LegalEntity"
import { FrontendPermissionModule } from "src/frontend/session/permissionModule"

const DEFAULT_VARS = {
  direction: DirectionFromOrigin.Past,
  filter: {
    status: STATUS_OPEN,
    includeMode: [ActionItemTargetMode.Assign],
  },
}

export function useActionItemsBadgeCountsQuery(poll: boolean = false, skip?: boolean) {
  const legalEntities = useLegalEntities()
  const actionItemsQuery = useListActionItemsBadgeCountsQuery({
    variables: {
      ...DEFAULT_VARS,
      origin: dateTimeHelper.todayIntervalOrigin(Interval.Month, 48),
      legalEntities,
    },
    skip: skip || !legalEntities.length,
    fetchPolicy: "cache-and-network",
  })
  const { data, loading } = actionItemsQuery

  // Poll for action items
  usePollingWithBackoff({
    query: actionItemsQuery,
    startCondition: !skip && poll,
    backoffMultiplier: 1.1,
  })

  return React.useMemo(
    () => ({
      counts: data?.listActionItemsBadgeCounts,
      loading,
    }),
    [data?.listActionItemsBadgeCounts, loading]
  )
}

export function useCurrentLegalEntityActionItemsBadgeCount(skip?: boolean) {
  const { currentOrganization, currentLegalEntity } = useSession<FrontendSession>()
  const legalEntities = [currentLegalEntity]
    .filter((le) => hasActionItemsPermissions(currentOrganization?.permissions))
    .map(mapActiveLE)
    .filter(defined)

  const { data, loading, refetch } = useListActionItemsBadgeCountsQuery({
    variables: {
      ...DEFAULT_VARS,
      origin: dateTimeHelper.todayIntervalOrigin(Interval.Month, 48),
      legalEntities,
    },
    skip: skip || !legalEntities.length,
  })

  const view = useViewVersion()
  React.useEffect(() => {
    if (!legalEntities.length || !view.viewVersion) return
    // Refetch Inbox badges on new View versions
    refetch()
  }, [legalEntities.length, refetch, view])

  useNotificationCreatedSubscription({
    onData: (subscriptionData) => {
      if (
        currentLegalEntity &&
        subscriptionData.data.data?.notificationCreated.id &&
        subscriptionData.data.data.notificationCreated.legalEntityId === currentLegalEntity.id
      ) {
        if (!legalEntities.length || !view.viewVersion) return
        refetch()
      }
    },
  })

  // when there are no action items the counts array will be empty
  return { counts: data?.listActionItemsBadgeCounts?.[0]?.count ?? 0, loading, refetch }
}

function useLegalEntities(): LegalEntityPermission[] {
  const session = useSession<FrontendSession>()
  const {
    sharingLegalEntity,
    currentOrganization,
    currentLegalEntity,
    isAffiliatedSession,
    affiliations,
  } = session

  if (sharingLegalEntity) return []

  if (isAffiliatedSession) {
    return affiliations.map(({ entity }) => mapActiveLE(entity))?.filter(defined)
  }

  return [currentLegalEntity]
    .filter((le) => hasActionItemsPermissions(currentOrganization?.permissions))
    .map(mapActiveLE)
    .filter(defined)
}

const mapActiveLE = (le: SessionLegalEntity | undefined) => {
  if (!le || !le.hasDashboardAccess() || !le.hasAccessToAspect(AspectCode.TransactionActionItems)) {
    return null
  }
  return {
    legalEntityId: le.id,
    canManageTransactions: true,
  }
}

const hasActionItemsPermissions = (
  permissions: Permissions<FrontendPermissionModule> | undefined
) => permissions?.hasReadPermission(FrontendPermissionModule.ActionItems)
